import { HISTORY_CASE_LABEL, HISTORY_EVENT_LABELS, HISTORY_FILTER_NONE, HISTORY_FILTER_VALUES } from '../constants';
import { formatAttemptTagDate } from '../AttemptsSection/utils';
import type { IdvHistoryItem } from '../types';
import type { AttemptRefView, HistoryFilterColumn, HistoryFilterValue, HistoryFilters } from './types';

export const HISTORY_FILTER_COLUMNS: HistoryFilterColumn[] = ['event', 'actor', 'attempt'];

export const emptyHistoryFilters = (): HistoryFilters => ({
  event: new Set(),
  actor: new Set(),
  attempt: new Set(),
});

export const cloneHistoryFilters = (filters: HistoryFilters): HistoryFilters => ({
  event: new Set(filters.event),
  actor: new Set(filters.actor),
  attempt: new Set(filters.attempt),
});

export const getPartnerWorkerNotesUrl = (workerId?: string | number): string => {
  const notesPath = window.location.pathname.replace(/\/show\/[^/]*$/, '/show/notes');
  if (notesPath.endsWith('/show/notes')) return notesPath;
  return workerId == null ? '/candidates/show/notes' : `/candidates/${workerId}/show/notes`;
};

export const getHistoryEventLabel = (event: string): string => {
  const known = HISTORY_EVENT_LABELS[event];
  if (known) return known;
  return event
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const getAttemptRefView = (attemptRef: string): AttemptRefView => {
  if (attemptRef.toLowerCase() === 'case') return { kind: 'case', label: HISTORY_CASE_LABEL };
  const ai = attemptRef.endsWith('+AI') || attemptRef.endsWith('+ai');
  const id = ai ? attemptRef.replace(/\+AI$/i, '') : attemptRef;
  return { kind: ai ? 'ai' : 'attempt', label: id.startsWith('#') ? id : `#${id}` };
};

export const getHistoryTimeLabel = (createdAt: string): string => (createdAt ? formatAttemptTagDate(createdAt) : '');

export const sortHistoryNewestFirst = (history: IdvHistoryItem[]): IdvHistoryItem[] =>
  [...history].sort((left, right) => right.created_at.localeCompare(left.created_at));

export const getHistoryFilterValue = (item: IdvHistoryItem, column: HistoryFilterColumn): string => {
  if (column === 'event') return getHistoryEventLabel(item.event);
  if (column === 'actor') return item.actor;
  return getAttemptRefView(item.attempt_ref).label;
};

export const isHistoryRowVisible = (item: IdvHistoryItem, filters: HistoryFilters): boolean =>
  HISTORY_FILTER_COLUMNS.every((column) => !filters[column].has(getHistoryFilterValue(item, column)));

export const getDistinctFilterValues = (
  history: IdvHistoryItem[],
  column: HistoryFilterColumn
): HistoryFilterValue[] => {
  const counts = new Map<string, number>();
  history.forEach((item) => {
    const value = getHistoryFilterValue(item, column);
    counts.set(value, (counts.get(value) ?? 0) + 1);
  });

  return [...counts.keys()]
    .sort((left, right) => {
      if (column !== 'attempt') return left.localeCompare(right);
      if (left === HISTORY_CASE_LABEL) return 1;
      if (right === HISTORY_CASE_LABEL) return -1;
      return Number(right.replace('#', '')) - Number(left.replace('#', ''));
    })
    .map((value) => ({ value, count: counts.get(value) ?? 0 }));
};

export const getActiveFilterColumns = (filters: HistoryFilters): HistoryFilterColumn[] =>
  HISTORY_FILTER_COLUMNS.filter((column) => filters[column].size > 0);

export const getFilterChipSummary = (
  history: IdvHistoryItem[],
  column: HistoryFilterColumn,
  deselected: Set<string>
): string => {
  const kept = getDistinctFilterValues(history, column)
    .map((entry) => entry.value)
    .filter((value) => !deselected.has(value));
  if (kept.length <= 2) return kept.join(', ') || HISTORY_FILTER_NONE;
  return HISTORY_FILTER_VALUES(kept.length);
};
